import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ButtonBase, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';
import BusyIcon from '~/modules/resourcing/common/components/BusyIcon';
import { useMeContext } from '~/modules/me';
import { getRTLPopperStyles } from '~/modules/common/util';
import { getTodayForUser } from '~/modules/common/dates/today';
import { DelaStarIcon } from '~/modules/common/components/Icons';
import { UserWithRoles } from '~/modules/common/components/User';
import {
  useGetResourceUser,
  useBulkAcceptAllocationsForResourceRequest
} from '~/modules/resourcing/common/hooks';
import { useAllocationChartContext } from '~/modules/projects/resourcing-plan/ResourcingPlanPage/contexts';
import ResourceRequestMatchItemSkeleton from './ResourceRequestMatchItemSkeleton';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: theme.spacing(0.75)
  },
  matchRoot: {
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.75, 0, 0, 0.75),
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.09)'
    }
  },
  buttonContainer: {
    display: 'block',
    width: '75%'
  },
  matchContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  matchScoreText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  delaStarIcon: {
    fill: theme.palette.Dela.darkIcon,
    fontSize: theme.spacing(1.5)
  },
  checkIcon: {
    fill: theme.palette.success.main
  },
  checkCircleShadow: {
    display: 'flex',
    borderRadius: theme.spacing(5),
    '&:hover': { boxShadow: '0px 0px 0px 10px rgba(38,210,147,0.04)' },
    height: 'fit-content',
    marginLeft: theme.spacing(1.5)
  }
}));

const useUserWithRolesStyles = makeStyles(theme => ({
  avatar: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    fontSize: theme.spacing(1)
  },
  rows: {
    display: 'flex'
  },
  row: {
    width: 'inherit'
  },
  nameRow: {
    display: 'inline-block',
    textAlign: 'start',
    fontSize: theme.spacing(1.625),
    fontWeight: theme.typography.fontWeightRegular,
    maxWidth: theme.spacing(12)
  }
}));

const useTooltipPopperStyles = makeStyles(theme => ({
  popper: () => ({
    ...getRTLPopperStyles({ theme }),
    display: 'flex'
  })
}));

export const ResourceRequestMatchItemMatch = ({
  resourceRequest,
  setAnchorEl,
  setPopoverResourceRequest,
  project
}) => {
  const me = useMeContext();
  const classes = useStyles();
  const userWithRolesClasses = useUserWithRolesStyles();
  const tooltipPopperClasses = useTooltipPopperStyles();
  const { formatMessage } = useIntl();
  const { id, resourceMatches } = resourceRequest;
  const [resourceMatch] = resourceMatches;
  const dataQeId = `${id}_ResourceRequestMatchItemMatch`;
  const checkMarkDataQeId = `${id}_ResourceRequestMatchItemMatchCheckmark`;
  const tooltipDataQeId = `${id}_ResourceRequestMatchItemMatchCheckmarkTooltip`;
  const acceptTooltipTitle = formatMessage({
    id: 'resourceAllocationItem.acceptResource'
  });
  const { allocationChartRef } = useAllocationChartContext();
  const {
    bulkAcceptAllocationsForResourceRequest
  } = useBulkAcceptAllocationsForResourceRequest();

  const handleAccept = useCallback(
    event => {
      event.stopPropagation();
      bulkAcceptAllocationsForResourceRequest({
        acceptAllocationInput: [
          {
            resourceRequestId: resourceRequest.id,
            userIds: resourceMatch ? [resourceMatch.userUri] : []
          }
        ],
        allocationChartRef
      });
    },
    [
      bulkAcceptAllocationsForResourceRequest,
      resourceRequest,
      allocationChartRef
    ]
  );

  const handlePopover = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
      setPopoverResourceRequest(resourceRequest);
    },
    [setAnchorEl, setPopoverResourceRequest, resourceRequest]
  );

  const { loading, resourceUsers } = useGetResourceUser({
    page: 1,
    pageSize: 1,
    userUris: resourceMatches[0].userUri,
    date: getTodayForUser(me)
  });

  const user = useMemo(
    () =>
      !loading && resourceUsers.length
        ? {
            ...resourceUsers[0],
            roles: undefined,
            projectRoles: undefined
          }
        : null,
    [loading, resourceUsers]
  );

  const hasEditAllowed =
    project.permittedActionUris.includes(
      'urn:replicon:project-action:edit-project'
    ) &&
    project.permittedActionUris.includes(
      'urn:replicon:project-action:edit-team'
    );

  return user ? (
    <div className={classes.root}>
      <ButtonBase
        data-qe-id={dataQeId}
        className={classes.buttonContainer}
        onClick={handlePopover}
      >
        <div className={classes.matchRoot}>
          <div className={classes.matchContainer}>
            <div className={classes.matchScoreText}>
              <Typography variant="caption" color="textPrimary">
                {formatMessage(
                  {
                    id: 'resourceAllocationItem.percentMatch'
                  },
                  {
                    percent: resourceMatches[0].score
                  }
                )}
              </Typography>
              <DelaStarIcon className={classes.delaStarIcon} />
            </div>
            <UserWithRoles
              qeIdTag={dataQeId}
              classes={userWithRolesClasses}
              user={user}
            />
          </div>
          <BusyIcon
            requestLoad={resourceRequest.load}
            availabilityPercentage={
              resourceMatch.availabilityPercentageInDateRange
            }
          />
        </div>
      </ButtonBase>
      {hasEditAllowed && (
        <ButtonBase
          data-qe-id={checkMarkDataQeId}
          className={classes.checkCircleShadow}
          onClick={handleAccept}
        >
          <Tooltip
            data-qe-id={tooltipDataQeId}
            title={acceptTooltipTitle}
            aria-label={acceptTooltipTitle}
            aria-hidden={false}
            placement="bottom-start"
            classes={tooltipPopperClasses}
          >
            <CheckCircle className={classes.checkIcon} />
          </Tooltip>
        </ButtonBase>
      )}
    </div>
  ) : (
    <ResourceRequestMatchItemSkeleton dataQeId={dataQeId} />
  );
};

ResourceRequestMatchItemMatch.propTypes = {
  resourceRequest: PropTypes.object.isRequired,
  setAnchorEl: PropTypes.func.isRequired,
  setPopoverResourceRequest: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired
};

export default ResourceRequestMatchItemMatch;
